import Component from 'vue-class-component';
import { Mixins, Prop, Emit } from 'vue-property-decorator';
import { WidgetTeacherSectionItem } from '@/homework/models/widget-teacher-section-item.model';
import { Section } from '@/courses/models/section.model';
import KEYS from '@/values/keys';
import { SECTION_TYPES, TYPE_BOARD_DRAFT_RESPONSE_OPTIONS, TYPE_TEXT_DRAFT_RESPONSE_OPTIONS } from '@/content/values/sections';
import EdHomeworkViewer from '@/homework/component/ed-homework-viewer/ed-homework-viewer.vue';
import EdListHomeworkHistoryComponent from '@/homework/component/ed-list-homework-history/ed-list-homework-history.component.vue';
import EdEdupadHistory from '@/components/common/ed-edupad/ed-edupad-history/ed-edupad-history.vue';

import {
  HOMEWORKS_EVENTS,
  HOMEWORK_STATUS,
  _HOMEWORKS_STORE,
  GRADING_SCALES,
  HOMEWORK_HISTORY_STATUS
} from '@/homework/values/homeworks.values';
import { WriteObjectMixin } from '@/courses/mixins/write-object.mixin';
import { HomeworkMixin } from '@/homework/mixins/homework.mixin';
import ToolService from '@/providers/tools';
import { SCREEN_MIRRORING_EVENTS } from '@/values/screen.mirroring.values';
import PrintMixin from '@/courses/mixins/print-mixin';


@Component({
  components: {
    EdEdupadHistory
  }
})
export default class EdHomeworkStudentSectionItem extends Mixins(HomeworkMixin, WriteObjectMixin, PrintMixin) {

  @Prop() readonly homework: WidgetTeacherSectionItem;
  @Prop() readonly courseId: number;

  public sectionTypes: any = SECTION_TYPES;
  public isSolving: boolean = false;
  private sectionsNotPrintable: Array<string> = [
    SECTION_TYPES.SPLIT_QUESTION_MATERIAL.code,
    SECTION_TYPES.SPLIT_SOLUTION_MATERIAL.code, 
    SECTION_TYPES.SPLIT_SOLUTION_PDF.code
  ]

  @Emit('onShowHistory')
  emitOnShowHistory() {
    return true;
  }

  get course() {
    return this.$store.state.HomeworksStore.studentWidgetHomeworks.courses.find(
      (cs:any) => cs.id == this.courseId);
  }

  get section(): Section {
    return this.homework.section;
  }

  get feedback(): any {
    return this.homework.feedback;
  }

  get gradingDetails(): any {
    return this.homework.gradingDetails;
  }

  get canPrint(): boolean {
    switch(this.section.type) {
      case SECTION_TYPES.LESSON_SPLIT_WRITE.code:
      case SECTION_TYPES.LESSON_SPLIT_TYPE.code:
        return this.getCanPrintSplit();
      break;
      case SECTION_TYPES.LESSON_MATERIAL.code:
        return false;
      break;
      default:
        return true;
      break;
    }
  }

  get urlToPrint(): string {

    /* Search the paper */
    let url = this.geUrlFromAssets(this.section.assetsInfo);

    if(!url) {
      /* Search in the problem */
      this.section.sections.forEach((s: Section) => {
        if(!this.sectionsNotPrintable.includes(s.type)) {
          url = this.geUrlFromAssets(s.assetsInfo);
        }
      });
    }
    return url;

  }

  get canSolve() {
    return this.homework.feedback ? true : false;
  }

  get grade() {
    let grade = null;
    if(this.section.homeworkStatus == HOMEWORK_STATUS.GRADED || this.section.homeworkStatus == HOMEWORK_STATUS.FINISHED) {
      return this.course.hasGrading ? this.section?.gradeName || this.section.homeworkGrade : this.section.homeworkGrade;
    }
    return grade;
  }

  get gradeDetails() {
    if(this.course.hasGrading){
      if(this.section.homeworkStatus == HOMEWORK_STATUS.GRADED || this.section.homeworkStatus == HOMEWORK_STATUS.FINISHED) {
        if (this.gradingDetails?.length == 1) {
          return `${this.$t('courses.grade.gradeW')}: ${this.gradingDetails[0].gradeName ? this.gradingDetails[0].gradeName : this.gradingDetails[0].type == GRADING_SCALES.PERCENTAGE ? `${this.gradingDetails[0].value}%` : this.gradingDetails[0].points }, 
            ${this.$t('courses.grade.points')}: ${this.gradingDetails[0].points}/${this.gradingDetails[0].itemPoints}, 
            ${this.gradingDetails[0].value}%`
        } else if (this.gradingDetails?.length > 1) {
          let grades = [];
          this.gradingDetails.forEach((el, i) => {
            grades.push(`${this.$t('courses.grade.gradeW')} Q${i+1}: ${el.gradeName ? el.gradeName : el.type == GRADING_SCALES.PERCENTAGE ? `${el.value}%` : el.points}, 
              ${this.$t('courses.grade.points')}: ${el.points}/${el.itemPoints}, 
              ${el.value}%`)
          })
          return grades
        } else {
          return this.section.homeworkGrade
        }
      }
    }else{
      return this.section.homeworkGrade
    }
  }

  get canResponse(): boolean {
    if(this.isLessonTextRespondedWithPdf(this.homework)) {
      return false;
    }
    return this.getCanAnswerWithObject(this.section);
  }

  get boardGroupHash(): string {   
    return this.homework.responseDraft && this.homework.responseDraft.boardGroup ? this.homework.responseDraft.boardGroup : null;
  }

  get canOpenWithCurrentVersion() : boolean {
    return  this.isDraftResponseOption()
            || !this.homework.responseDraft
            || !this.validateDraftBoardOptions(this.section, TYPE_BOARD_DRAFT_RESPONSE_OPTIONS);
  }

  get name(): string {
    return ToolService.exitSpecialChars(this.section.name);
  }

  get isStudent(): boolean {
    return KEYS.ROLES_MAPPED.BUYER === this.$store.state.AuthStore.user.role;
  }

  get studentId(): number {
    if (this.homework?.student?.id) {
      return this.homework.student.id;
    } else if (this.isStudent) {
      return this.$store.state.AuthStore.user.id;
    }
  }

  get historyTitle(){
    return this.$t('homeworks.historyTitle', { name: this.section.name })
  }

  get historyCollection(){
    return this.homework.responseDraft.answers.collection;
  }

  get historyDocumentId(){
    return this.homework.responseDraft.answers.documents[0];
  }

  get hasEdupadHistory(){
    return this.homework.section.type === 'LESSON_SPLIT_TYPE' && this.homework.responseDraft
  }

  private isDraftResponseOption(): boolean {
    return this.homework.responseDraft && (
      // Board
      (this.validateDraftBoardOptions(this.section, TYPE_BOARD_DRAFT_RESPONSE_OPTIONS)
        && this.homework.responseDraft.type === SECTION_TYPES.LESSON_BOARD.code)
      // Text
      || this.validateDraftBoardOptions(this.section, TYPE_TEXT_DRAFT_RESPONSE_OPTIONS)
    );
  }

  public sendResponse() {
    if (!this.homework.response) {
      this.$edEventBus.$emit(HOMEWORKS_EVENTS.OPEN_SEND_RESPONSE_HOMEWORK,
          {homework: this.section, course: this.course});
    }
  }

  public openViewer(info: any, canResponse: boolean, isFeedback: boolean = false, history: any = {}) {
    let feedbackDraft = info.homeworkDraftAssetInfo;
    this.$EdModal.createModal(EdHomeworkViewer, {
      section: {
        id: info.sectionId,
        name: info.name,
        assetsInfo: info.assetsInfo,
        type: info.type,
        text: info.text,
        sectionId: info.sectionId,
        homework: true,
        classId: this.homework.classId,
        sections: info.sections,
        homeworkStatus: info.homeworkStatus,
        template: info.template,
        sectionMaterial: info.sectionMaterial,
        status: info.status,
        homeworkDraftAssetInfo: feedbackDraft ? feedbackDraft : null,
        version: info.version
      },
      response: this.homework.response,
      responseDraft: this.homework.responseDraft ? this.homework.responseDraft : null,
      feedback: this.getFeedbackSection(),
      courseId: this.courseId,
      course: this.course,
      viewer: this.getViewer(info),
      canResponse,
      isFeedback,
      history: history,
      student: this.homework.student ? this.homework.student : null
    });
  }

  public getFeedbackSection() {
    if(this.feedback) {
      return {
        ...this.feedback,
        sectionId: this.section.sectionId,
        assetsInfo: [this.feedback.homeworkAssetInfo]
      }
    } else {
      return null;
    }
  }

  public openHomework(emitMirroringEvent : boolean = true) {
    if(!this.canOpenWithCurrentVersion){
      this.showCantOpenHomeworkModal();
      return;
    }

    let canResponse = this.homework.response ? false : true;
    this.openViewer(this.section, canResponse);

    if( emitMirroringEvent ) {
      var target = this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_HOMEWORK_VIEWER);
      this.$edEventBus.$emit( SCREEN_MIRRORING_EVENTS.CLICK, this, {target:target});
    }  
  }

  public openFeedback() {
    this.openViewer(this.getFeedbackSection(), false, true);
  }

  public solveHomework() {
    this.isSolving = true;
    this.$store.dispatch(_HOMEWORKS_STORE.ACTIONS.SET_HOMEWORK_SOLVED, this.homework).finally(() => {
      this.isSolving = false;
    });
  }

  async print() {
    if(this.canPrint && this.urlToPrint) {
      await this.handlePrinting(this.urlToPrint);
    }
  }

  geUrlFromAssets(assetsInfo: any) {
    if(assetsInfo && assetsInfo.length > 0) {
      if(assetsInfo[0].pdfUrl) {
        return assetsInfo[0].pdfUrl;
      }
      if(assetsInfo[0].url) {
        return assetsInfo[0].url;
      }
    }
    return null;
  }

  private getViewer(info: any) {
    if(this.isLessonTextRespondedWithPdf(this.homework)) {
      return SECTION_TYPES[SECTION_TYPES.LESSON_PDF.code]['viewerHomework'];
    }
    return SECTION_TYPES[info.type].viewerHomework;
  }

  private target(screenMirroringEventName:string) {
    return screenMirroringEventName.replace('{sectionId}', this.section.sectionId.toString());
  }

  private openHomeworkWithoutMirroring() {
    this.openHomework(false);
  }

  private getCanPrintSplit(): boolean {
    let canPrint: boolean = false;
    if(this.section.assetsInfo) {
      return true;
    }
    this.section.sections.forEach((section: Section) => {
      if(!this.sectionsNotPrintable.includes(section.type)) {
        canPrint = true;
      }
    });
    return canPrint;
  }

  public openHistory() {
    if(this.section.type == 'LESSON_SPLIT_TYPE' && this.homework.responseDraft.answers){
      this.$EdModal.createBootstrapModal(EdEdupadHistory, {
        collection: this.historyCollection,
        documentId: this.historyDocumentId,
        title: this.historyTitle
      })
    } else {
      let data = {
        classId: this.course.classId,
        sectionId: String(this.section.id),
        sectionName: this.section.name,
        historyStatus: HOMEWORK_HISTORY_STATUS.RESPONSE,
        studentId: this.studentId,
        title: this.section.name
      } 
      this.$EdModal.createModal(EdListHomeworkHistoryComponent, {data});
    }
  }

  public openSnapShot(params) {
    if (String(params.sectionId) === String(this.section.id)){
      this.openViewer(this.section, true, true,
          {...params, show:true});
    }
  }

  get showHistory(): any {
    if ((this.homework.responseDraft && this.homework.responseDraft.status === 'DRAFT')){
      return true;
    }
    return false;
  }

  created(): void {
    this.$edEventBus.$on(this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_HOMEWORK_VIEWER), this.openHomeworkWithoutMirroring);
    this.$edEventBus.$on(HOMEWORKS_EVENTS.OPEN_HOMEWORK_SNAPSHOT, this.openSnapShot);
  }
  //
  beforeDestroy(): void {
    this.$edEventBus.$off(this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_HOMEWORK_VIEWER));
    this.$edEventBus.$off(HOMEWORKS_EVENTS.OPEN_HOMEWORK_SNAPSHOT, this.openSnapShot);
  }
}
